import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  useDisclosure,
  IconButton,
  useToast,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { MdDeleteOutline, MdEdit } from "react-icons/md";
import axios, { AxiosResponse } from "axios";

const Faq = () => {
  type Faq = {
    id: string;
    question: string;
    answer: string;
    category: string;
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [faqList, setFaqList] = useState<Faq[]>([]);
  const [faqData, setFaqData] = useState({
    id: "",
    question: "",
    answer: "",
    category: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true); // For loading spinner during data fetch

 const fetchFaq = async () => {
  try {
    setIsFetching(true); // Start fetching
    const token = localStorage.getItem("AuthToken"); // Retrieve the auth token
    const res: AxiosResponse<any> = await axios.get(
      "https://api.pureworker.com/api/get-faq",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (res.status === 200) {
      const customerFaqs = res.data.data.customer || []; // Safely access customer array
      const providerFaqs = res.data.data.provider || []; // Safely access provider array
      const bothFaqs = res.data.data.both || []; // Safely access both array

      // Combine all FAQ arrays
      const allFaqs = [...customerFaqs, ...providerFaqs, ...bothFaqs];
      
      setFaqList(allFaqs); // Set faqList to combined array
    } else {
      console.error("Unexpected response structure:", res);
      setFaqList([]); // Set to empty array in case of failure
    }
  } catch (err) {
    console.error("Failed to fetch FAQs:", err);
    setFaqList([]); // Set to empty array on error
  } finally {
    setIsFetching(false); // End fetching
  }
};

  useEffect(() => {
    fetchFaq(); // Fetch FAQ data on component mount
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFaqData({
      ...faqData,
      [name]: value,
    });
  };

  const handleCategoryChange = (event: {
    target: { value: string };
  }) => {
    setFaqData({
      ...faqData,
      category: event.target.value,
    });
  };

  const handleEdit = (item: Faq) => {
    setFaqData(item);
    onOpen();
  };

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem("AuthToken");
      await axios.delete(`https://api.pureworker.com/api/delete-faq/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast({
        title: "FAQ deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });

      // Re-fetch FAQ list after deletion
      fetchFaq();
    } catch (err) {
      toast({
        title: "Error deleting FAQ.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.error("Failed to delete FAQ:", err);
    }
  };

  const handleAddFaq = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("AuthToken");
      const res = await axios.post(
        "https://api.pureworker.com/api/create-faq",
        {
          question: faqData.question,
          answer: faqData.answer,
          category: faqData.category,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 201) {
        toast({
          title: "FAQ added successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });

        // Re-fetch FAQ list after adding
        fetchFaq();
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error adding FAQ.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.error("Failed to add FAQ:", err);
    }
    setIsLoading(false);
  };

const handleUpdateFaq = async () => {
  setIsLoading(true);
  try {
    const token = localStorage.getItem("AuthToken");

    const payload = {
      id: faqData.id,
      question: faqData.question,
      answer: faqData.answer,
      category: faqData.category,
    };

    const res = await axios.post(
      `https://api.pureworker.com/api/create-faq`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (res.status === 200) {
      toast({
        title: "FAQ updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });

      fetchFaq();
      onClose();
    }
  } catch (err) {
    toast({
      title: "Error updating FAQ.",
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
    console.error("Failed to update FAQ:", err);
  } finally {
    setIsLoading(false);
  }
};

const [currentPage, setCurrentPage] = useState(1);
const itemsPerPage = 10;

  return (
    <Box p={5}>
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Box as="p" fontWeight="bold" fontSize="2xl">
          FAQ Area
        </Box>
        <Button
          leftIcon={<FaPlus />}
          colorScheme="purple"
          onClick={() => {
            setFaqData({ id: "", question: "", answer: "", category: "" });
            onOpen();
          }}
        >
          Add FAQ
        </Button>
      </Box>

      {/* Show spinner while fetching data */}
      {isFetching ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Spinner size="xl" />
        </Box>
      ) : (
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>No</Th>
              <Th>Question</Th>
              <Th>Answer</Th>
              <Th>Category</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
  {faqList
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((item, index) => (
      <Tr key={item.id}>
        <Td>{(currentPage - 1) * itemsPerPage + index + 1}</Td>
        <Td>{item.question}</Td>
        <Td>{item.answer}</Td>
        <Td>{item.category}</Td>
        <Td>
          <IconButton
            icon={<MdEdit />}
            aria-label="Edit"
            onClick={() => handleEdit(item)}
            mr={2}
            colorScheme="purple"
          />
          <IconButton
            icon={<MdDeleteOutline />}
            aria-label="Delete"
            onClick={() => handleDelete(item.id)}
            colorScheme="red"
          />
        </Td>
      </Tr>
    ))}
</Tbody>

        </Table>
      )}
<Box display="flex" justifyContent="space-between" mt={4}>
  <Button
    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
    isDisabled={currentPage === 1}
  >
    Previous
  </Button>
  <Box>
    Page {currentPage} of {Math.ceil(faqList.length / itemsPerPage)}
  </Box>
  <Button
    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(faqList.length / itemsPerPage)))}
    isDisabled={currentPage === Math.ceil(faqList.length / itemsPerPage)}
  >
    Next
  </Button>
</Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{faqData.id ? "Edit FAQ" : "Add FAQ"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Question"
              name="question"
              value={faqData.question}
              onChange={handleChange}
              mb={4}
            />
            <Textarea
              placeholder="Answer"
              name="answer"
              value={faqData.answer}
              onChange={handleChange}
              mb={4}
            />
            <Select
              placeholder="Select category"
              value={faqData.category}
              onChange={handleCategoryChange}
            >
              <option value="customer">Customer</option>
              <option value="provider">Service Provider</option>
              <option value="both">Both</option>
            </Select>
          </ModalBody>

          <ModalFooter>
            {faqData.id ? (
              <Button
                colorScheme="purple"
                mr={3}
                onClick={handleUpdateFaq}
                isLoading={isLoading}
              >
                Update
              </Button>
            ) : (
              <Button
                colorScheme="purple"
                mr={3}
                onClick={handleAddFaq}
                isLoading={isLoading}
              >
                Add
              </Button>
            )}
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Faq;
